import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, Clock, User, List } from 'lucide-react';
import './styles/ApprovalsPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pending-change values can be strings, numbers, null or subtask lists.
const formatChangeValue = (value) => {
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) return value.map((s) => (typeof s === 'string' ? s : `${s.done ? '☑' : '☐'} ${s.title}`)).join(', ') || '—';
  return String(value);
};


export default function ApprovalsPage({ user }) {
  const [pendingChanges, setPendingChanges] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTeams();
  }, []);

  useEffect(() => {
    if (selectedTeam) {
      fetchPendingChanges();
    }
  }, [selectedTeam]);

  const fetchTeams = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const ownerTeams = response.data.filter(t => t.owner_id === user.id);
      setTeams(ownerTeams);
      if (ownerTeams.length > 0) {
        setSelectedTeam(ownerTeams[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch teams');
    }
  };

  const fetchPendingChanges = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/teams/${selectedTeam}/pending-changes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingChanges(response.data);
    } catch (error) {
      console.error('Failed to fetch pending changes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (changeId) => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/pending-changes/${changeId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Change approved successfully!');
      fetchPendingChanges();
    } catch (error) {
      toast.error('Failed to approve change');
    }
  };

  const handleReject = async (changeId) => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/pending-changes/${changeId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Change rejected');
      fetchPendingChanges();
    } catch (error) {
      toast.error('Failed to reject change');
    }
  };

  const getChangeDescription = (change) => {
    switch (change.change_type) {
      case 'status':
        return `Change status from "${change.old_value}" to "${change.new_value}"`;
      case 'assignment':
        return change.new_value 
          ? `Reassign task to another member`
          : `Unassign task`;
      case 'details':
        const fields = Object.keys(change.changes || {});
        return `Update ${fields.join(' and ')}`;
      default:
        return 'Update task';
    }
  };

  if (teams.length === 0) {
    return (
      <div className="approvals-page">
        <div className="empty-state">
          <h2>No Teams Owned</h2>
          <p>You need to be a team owner to access approvals.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="approvals-page">
      <div className="approvals-header">
        <div>
          <h1>Pending Approvals</h1>
          <p>Review and approve task changes from your team</p>
        </div>
        <Badge 
          variant="secondary" 
          style={{ 
            background: pendingChanges.length > 0 ? '#d1f4e0' : '#E5E7EB',
            color: pendingChanges.length > 0 ? '#0f6b4f' : '#6B7280',
            border: pendingChanges.length > 0 ? '1px solid #1a936f' : '1px solid #d1d5db',
            fontSize: '0.875rem',
            padding: '0.5rem 1rem'
          }}
        >
          <Clock size={16} style={{ marginRight: '0.5rem' }} />
          {pendingChanges.length} Pending
        </Badge>
      </div>

      {teams.length > 1 && (
        <div className="team-selector">
          {teams.map(team => (
            <button
              key={team.id}
              className={`team-tab ${selectedTeam === team.id ? 'active' : ''}`}
              onClick={() => setSelectedTeam(team.id)}
            >
              {team.name}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="loading-state">Loading approvals...</div>
      ) : pendingChanges.length === 0 ? (
        <div className="empty-state">
          <CheckCircle size={48} style={{ color: '#10B981', marginBottom: '1rem' }} />
          <h2>All Caught Up!</h2>
          <p>No pending changes to review at the moment.</p>
        </div>
      ) : (
        <div className="approvals-content">
          <section className="approval-section">
            <h2>
              <List size={20} />
              Pending Changes ({pendingChanges.length})
            </h2>
            <div className="approval-list">
              {pendingChanges.map(change => (
                <Card key={change.id} className="approval-card">
                  <div className="approval-card-header">
                    <div>
                      <h3>{change.task_title || 'Task'}</h3>
                      <p className="requester-info">
                        Requested by <strong>{change.requester_name || 'Team Member'}</strong>
                      </p>
                    </div>
                    <Badge variant="secondary">
                      {change.change_type === 'status' && 'Status Change'}
                      {change.change_type === 'assignment' && 'Reassignment'}
                      {change.change_type === 'details' && 'Details Update'}
                    </Badge>
                  </div>
                  
                  <div className="change-description">
                    <p>{getChangeDescription(change)}</p>
                    {change.change_type === 'details' && change.changes && (
                      <div className="change-details">
                        {Object.entries(change.changes).map(([field, values]) => (
                          <div key={field} className="change-detail-item">
                            <strong>{field.replace('_', ' ')}:</strong>
                            <span className="old-value">"{formatChangeValue(values.old)}"</span>
                            <span>→</span>
                            <span className="new-value">"{formatChangeValue(values.new)}"</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="approval-actions">
                    <Button
                      onClick={() => handleApprove(change.id)}
                      className="approve-btn"
                    >
                      <CheckCircle size={16} />
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleReject(change.id)}
                      className="reject-btn"
                    >
                      <XCircle size={16} />
                      Reject
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
